import { StateCreator } from 'zustand';
import type { EmployeeType } from '../../services/api/models/EmployeeType';
import type { ReportPageSizeOption } from '../../utils/reportDisplay';

export interface PayrollFilters {
  clinicId: string | null;
  /** ISO date strings (yyyy-mm-dd); both required before fetching. */
  startDate: string | null;
  endDate: string | null;
  /** Ordered selection; an empty array means all employee types. */
  employeeTypes: EmployeeType[];
  search: string;
  /** Exclude imported patients from payroll results by default. */
  hideImportedPatients?: boolean;
  /** Exclude visits dated before the confirmed take-over day on their admission (Visit.visit_date). */
  excludePreTakeover?: boolean;
}

/** The paged payroll lists; each remembers its own rows-per-page choice. */
export type PayrollList = 'unpaid' | 'paid' | 'detail';

export type PayrollPageSizes = Record<PayrollList, ReportPageSizeOption>;

export interface PayrollSlice {
  payrollFilters: PayrollFilters;
  initialDefaultsApplied: boolean;
  /** Session-only (not persisted): resets to 25 on reload. */
  payrollPageSizes: PayrollPageSizes;
  setPayrollFilters: (filters: Partial<PayrollFilters>) => void;
  setInitialDefaultsApplied: (applied: boolean) => void;
  setPayrollPageSize: (list: PayrollList, size: ReportPageSizeOption) => void;
}

const DEFAULT_PAYROLL_FILTERS: PayrollFilters = {
  clinicId: null,
  startDate: null,
  endDate: null,
  employeeTypes: [],
  search: '',
  hideImportedPatients: true,
  excludePreTakeover: true,
};

const DEFAULT_PAYROLL_PAGE_SIZES: PayrollPageSizes = {
  unpaid: 25,
  paid: 25,
  detail: 25,
};

export const payrollSlice: StateCreator<PayrollSlice> = set => ({
  payrollFilters: DEFAULT_PAYROLL_FILTERS,
  initialDefaultsApplied: false,
  payrollPageSizes: DEFAULT_PAYROLL_PAGE_SIZES,
  setPayrollFilters: (filters: Partial<PayrollFilters>) =>
    set(state => ({
      payrollFilters: { ...state.payrollFilters, ...filters },
    })),
  setInitialDefaultsApplied: (applied: boolean) =>
    set({ initialDefaultsApplied: applied }),
  setPayrollPageSize: (list: PayrollList, size: ReportPageSizeOption) =>
    set(state => ({
      payrollPageSizes: { ...state.payrollPageSizes, [list]: size },
    })),
});
